import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { T } from "../../app/tokens.js";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";

const DIMENSIONS = [
  { key: "total", label: "Total" },
  { key: "department", label: "Department" },
  { key: "market", label: "Market" },
  { key: "region", label: "Region" },
  { key: "workMode", label: "Work mode" },
];

const formatMonth = (value) => new Date(`${value}T00:00:00Z`).toLocaleDateString(
  "en-US",
  { month: "short", year: "numeric", timeZone: "UTC" },
);

export function OpenRolesMonthlyTrend({
  snapshots = [],
  loading = false,
  error = null,
  showMarkets = true,
  showOfficeBased = true,
}) {
  const [dimension, setDimension] = useState("total");
  const [item, setItem] = useState("");

  const availableDimensions = useMemo(
    () => DIMENSIONS.filter((entry) => (
      (showMarkets || !["market", "region"].includes(entry.key))
      && (showOfficeBased || entry.key !== "workMode")
    )),
    [showMarkets, showOfficeBased],
  );
  const currentDimension = availableDimensions.some((entry) => entry.key === dimension)
    ? dimension
    : "total";

  const items = useMemo(() => {
    if (!snapshots.length) return [];
    const field = currentDimension === "department" ? "departments"
      : currentDimension === "market" ? "markets"
        : currentDimension === "region" ? "regions" : null;
    if (!field) return [];
    const all = new Map();
    for (const snapshot of snapshots) {
      for (const row of snapshot[field] ?? []) {
        if (!all.has(row.name)) all.set(row.name, row.name);
      }
    }
    return [...all.values()].sort((a, b) => a.localeCompare(b));
  }, [snapshots, currentDimension]);

  const selectedItem = items.includes(item) ? item : items[0] ?? "";
  const chartData = useMemo(() => snapshots.map((snapshot) => {
    const selectedDimension = snapshot[currentDimension === "department" ? "departments"
      : currentDimension === "market" ? "markets"
        : currentDimension === "region" ? "regions" : ""] ?? [];
    const selected = selectedDimension.find((entry) => entry.name === selectedItem);
    return {
      month: snapshot.month,
      total: snapshot.total,
      ...(currentDimension === "department" && selected
        ? { openRoles: selected.total } : {}),
      ...(currentDimension === "market" && selected
        ? { openRoles: selected.openRoles } : {}),
      ...(currentDimension === "region" && selected
        ? { openRoles: selected.openRoles } : {}),
      ...(currentDimension === "workMode"
        ? { officeBased: snapshot.officeBased, remote: snapshot.remote } : {}),
    };
  }), [snapshots, currentDimension, selectedItem]);

  const lines = currentDimension === "total"
    ? [{ key: "total", label: "Open roles", color: T.blue }]
    : currentDimension === "workMode"
      ? [
          ...(showOfficeBased ? [{ key: "officeBased", label: "Office-based", color: T.blue }] : []),
          { key: "remote", label: "Remote", color: T.purple ?? T.mid },
        ]
      : [{ key: "openRoles", label: selectedItem, color: T.blue }];
  const intent = useChartHoverIntent(JSON.stringify([currentDimension, selectedItem, chartData, loading, error]));

  return (
    <section
      aria-label="Open Roles monthly trend"
      data-testid="open-roles-monthly-trend"
      style={{
        background: T.card,
        border: `1px solid ${T.border ?? T.lightGray}`,
        borderRadius: 12,
        padding: "18px 20px",
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: T.dark, fontSize: T.fsMd, fontWeight: T.fwBold }}>Monthly trend</h2>
          <p style={{ margin: "5px 0 0", color: T.mid, fontSize: T.fsXs }}>
            History starts with the first available snapshot. Earlier months are not available.
          </p>
        </div>
        {!loading && !error && snapshots.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} aria-label="Trend breakdown">
            {availableDimensions.map((entry) => (
              <button
                type="button"
                key={entry.key}
                data-testid={`open-roles-trend-dimension-${entry.key}`}
                aria-pressed={currentDimension === entry.key}
                onClick={() => { setDimension(entry.key); setItem(""); }}
                style={{
                  border: `1px solid ${currentDimension === entry.key ? T.blue : T.border ?? T.lightGray}`,
                  background: currentDimension === entry.key ? T.blue : T.card,
                  color: currentDimension === entry.key ? T.white : T.mid,
                  borderRadius: 7,
                  padding: "6px 10px",
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  cursor: "pointer",
                }}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: T.fsXs, color: T.mid }}>
          <span>{currentDimension === "department" ? "Department" : currentDimension === "market" ? "Market" : "Region"}</span>
          <select
            aria-label={`Select ${currentDimension}`}
            data-testid="open-roles-trend-item"
            value={selectedItem}
            onChange={(event) => setItem(event.target.value)}
            style={{ border: `1px solid ${T.border ?? T.lightGray}`, background: T.white, color: T.dark, borderRadius: 6, padding: "5px 8px" }}
          >
            {items.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
      )}

      {loading && <div style={{ padding: "42px 8px", color: T.mid }} data-testid="open-roles-trend-loading">Loading monthly history…</div>}
      {!loading && error && <div style={{ padding: "28px 8px", color: T.mid }} data-testid="open-roles-trend-error">Monthly history is temporarily unavailable.</div>}
      {!loading && !error && snapshots.length === 0 && (
        <div style={{ padding: "34px 8px", color: T.mid }} data-testid="open-roles-trend-empty">
          No monthly snapshots have been captured yet. History will begin with the first successful month-end capture.
        </div>
      )}
      {!loading && !error && snapshots.length > 0 && (
        <>
          <div
            {...intent.containerProps}
            data-testid="open-roles-trend-chart"
            data-chart-months={JSON.stringify(chartData.map((row) => row.month))}
            style={{ width: "100%", height: 260, marginTop: 12 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart {...intent.chartProps} data={chartData} margin={{ top: 10, right: 18, bottom: 4, left: 2 }}>
                <CartesianGrid stroke={T.border ?? T.lightGray} strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  tick={{ fontSize: T.fsXxs, fill: T.mid }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  width={42}
                  tick={{ fontSize: T.fsXxs, fill: T.mid }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals
                />
                <Tooltip {...intent.tooltipProps} labelFormatter={formatMonth} />
                {lines.length > 1 && <Legend />}
                {lines.map((line) => (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    name={line.label}
                    stroke={line.color}
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p style={{ margin: "4px 0 0", color: T.light, fontSize: T.fsXxs }}>
            {snapshots.length === 1
              ? `First captured month: ${formatMonth(snapshots[0].month)}`
              : `${snapshots.length} captured months · first month: ${formatMonth(snapshots[0].month)}`}
          </p>
        </>
      )}
    </section>
  );
}